<script lang="ts">
	import {
		DoubleDeviceButton,
		DeviceButton,
		Text,
		DoubleDeviceButtonV2,
		DeviceButtonV2,
		type DeviceButtonV2PropsTypes,
		type DoubleDeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import { callBluTRV, getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import { mathNormalize } from 'src/helpers/utilities';
	//services
	import { format } from 'svelte-i18n';
	//Types
	import type { Device } from 'src/types/Device';
	import type { BluTRVInterfaceData } from 'src/types/TRVComponent';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { celsius_to_fahrenheit, fahrenheit_to_celsius } from 'src/helpers/interface_helpers';

	export let device: Device, statuses: BluTRVInterfaceData[];
	export let useV2Components = false;
	export let devBtnsV2Props: {
		deviceButtonV2?: Partial<DeviceButtonV2PropsTypes>;
		doubleDeviceButtonV2?: Partial<DoubleDeviceButtonV2PropsTypes>;
	} = {
		deviceButtonV2: { size: 's' },
		doubleDeviceButtonV2: { size: 's' },
	};

	const unit = device?.ht_ui?.temperature_unit || 'C';
	let waitForTargetTempResponse = false;
	let sendTimeout: NodeJS.Timeout;
	let targetLock = false;
	let targetValue: number = null;

	const onChange = async (e: Event, stepValue: number) => {
		targetLock = true;
		e.preventDefault();
		e.stopPropagation();
		if (targetStatus && targetValue == null) {
			targetValue = Math.floor(targetStatus.event);
		}
		targetValue = mathNormalize(
			Number((+targetValue + +stepValue).toFixed(1)),
			enableTrv ? (unit === 'C' ? 4 : Math.round(celsius_to_fahrenheit(4))) : 0,
			enableTrv ? (unit === 'C' ? 30 : Math.round(celsius_to_fahrenheit(30))) : 100,
		);
		clearTimeout(sendTimeout);
		sendTimeout = setTimeout(sendChange, 1000);
	};

	const sendChange = async () => {
		waitForTargetTempResponse = true;
		let res: SettingRequest;
		if (enableTrv) {
			res = await callBluTRV(device, 'Trv.SetTarget', {
				id: 0,
				target_C: unit === 'C' ? targetValue : fahrenheit_to_celsius(targetValue),
			});
		} else {
			res = await callBluTRV(device, 'Trv.SetPosition', {
				id: 0,
				pos: unit === 'C' ? targetValue : fahrenheit_to_celsius(targetValue),
			});
		}
		if (!res.isok) {
			targetLock = false;
			onChangeTargetValue(targetStatus?.event);
		}
		waitForTargetTempResponse = false;
	};

	const onChangeTargetValue = (value: number) => {
		if (targetValue == value) targetLock = false;
		if (value != null && !targetLock && targetValue != value) {
			targetValue = value;
		}
	};

	$: enableTrv = getTrvComponentByDevice(device)?.config?.['trv:0']?.enable;
	$: targetStatus = statuses.find(
		(i) => i.name == (enableTrv ? 'target_temperature' : 'valve position'),
	);
	$: onChangeTargetValue(targetStatus?.event);
</script>

{#if targetStatus}
	{#if useV2Components}
		<DoubleDeviceButtonV2
			id="doubledevicebutton-otmmgpm"
			isLoading={waitForTargetTempResponse}
			isActive
			aria-label={$format('aria_label_set_target_temperature', {
				default: 'Set Target Temperature',
			})}
			{...devBtnsV2Props.deviceButtonV2}
		>
			<DeviceButtonV2
				aria-label={$format('button_decrease_target_temperature', {
					default: 'Decrease Target Temperature',
				})}
				id="devicebutton-qb51hd3"
				slot="buttonLeft"
				icon="fas fa-minus"
				on:click={(e) => onChange(e, -(enableTrv ? 0.1 : 1))}
				{...devBtnsV2Props.doubleDeviceButtonV2}
			/>
			<svelte:fragment slot="content">
				<Text styleType="body-extra-small" class="text-bold mb-xxs width-full" noWrap>
					{typeof targetValue === 'string' ? targetValue : targetValue?.toFixed(1)}
				</Text>
				<Text styleType="tiny">{`${targetStatus.unit || ''}`}</Text>
			</svelte:fragment>
			<DeviceButtonV2
				aria-label={$format('button_increase_target_temperature', {
					default: 'Increase Target Temperature',
				})}
				id="devicebutton-jyx1rmc"
				slot="buttonRight"
				icon="fas fa-plus"
				variant="danger"
				on:click={(e) => onChange(e, enableTrv ? 0.1 : 1)}
				{...devBtnsV2Props.doubleDeviceButtonV2}
			/>
		</DoubleDeviceButtonV2>
	{:else}
		<DoubleDeviceButton
			id="doubledevicebutton-otmmgpm"
			isLoading={waitForTargetTempResponse}
			isActive
			aria-label={$format('aria_label_set_target_temperature', {
				default: 'Set Target Temperature',
			})}
		>
			<DeviceButton
				aria-label={$format('button_decrease_target_temperature', {
					default: 'Decrease Target Temperature',
				})}
				id="devicebutton-qb51hd3"
				slot="buttonLeft"
				icon="fas fa-minus"
				on:click={(e) => onChange(e, -(enableTrv ? 0.1 : 1))}
			/>
			<svelte:fragment slot="content">
				<Text styleType="controls-large" class="text-bold">
					{typeof targetValue === 'string' ? targetValue : targetValue?.toFixed(1)}
				</Text>
				<Text styleType="controls-small">{`${targetStatus.unit || ''}`}</Text>
			</svelte:fragment>
			<DeviceButton
				aria-label={$format('button_increase_target_temperature', {
					default: 'Increase Target Temperature',
				})}
				id="devicebutton-jyx1rmc"
				slot="buttonRight"
				icon="fas fa-plus"
				variant="danger"
				on:click={(e) => onChange(e, enableTrv ? 0.1 : 1)}
			/>
		</DoubleDeviceButton>
	{/if}
{/if}
